import React from 'react';
import { Box, DrawerV2, Flex, IconButton } from '@withjoy/joykit';
import { Close } from '@withjoy/joykit/icons';
import { ValueProp, ValuePropCardsContainerArgs, ValuePropsTopBarItemsArgs } from './ValuePropsTopBar.types';
import { ReactComponent as ChevronDownSvg } from '@assets/icons/down-chevron.svg';
import { useValuePropsTopBar } from './ValuePropsTopBar.controller';
import {
  StyledMainContainer,
  StyledTitle,
  StyledValuePropCardsContainer,
  StyledValuePropBarContainer,
  styles,
  StyledValuePropCard,
  StyledValuePropBarVariantWrapper,
  StyledValuePropCardImg,
  drawerOverrides,
  StyledValuePropBarVariantContainer
} from './ValuePropsTopBar.styles';
import { ValuePropBarVariantArgs, ValuePropCardArgs } from './ValuePropsTopBar.types';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { useTelemetryPageEvent } from '@shared/utils/hooks/useTelemetryPageEvent';
import { StorefrontNavigator } from '../StorefrontNavigator';
import { useShopStorefrontContext } from '../ShopStorefrontProvider';
import FindLink from '../FindLink/FindLink';

const ValuePropsTopBar = () => {
  const {
    toggleDrawer,
    isDrawerOpen,
    activeValuePropVariant,
    cardsValueProps,
    fullGradientString,
    elementsAmount,
    handleOnValuePropClick,
    shouldShowStorefrontToggle,
    trackValuePropDrawerPage,
    showValuePropsTopBar,
    closeValuePropsTopBar
  } = useValuePropsTopBar();
  return showValuePropsTopBar ? (
    <StyledMainContainer __css={styles.mainContainer}>
      {shouldShowStorefrontToggle && (
        <Box __css={styles.storefrontNavigatorPlacement}>
          <StorefrontNavigator colorVariant="light" />
        </Box>
      )}
      <DrawerV2 isOpen={isDrawerOpen} anchor={'top'} onClose={toggleDrawer} size={'full'} overrides={drawerOverrides}>
        <DrawerV2.CloseButton />
        <DrawerV2.Body __css={styles.drawerBody}>
          <ValuePropCardsContainer cardsValueProps={cardsValueProps} onValuePropCardClick={handleOnValuePropClick} valuePropDrawerPageTracker={trackValuePropDrawerPage} />
        </DrawerV2.Body>
      </DrawerV2>
      <ValuePropsTopBarItems
        cardsValueProps={cardsValueProps}
        toggleDrawer={toggleDrawer}
        activeValuePropVariant={activeValuePropVariant}
        fullGradientString={fullGradientString}
        elementsAmount={elementsAmount}
        closeValuePropsTopBar={closeValuePropsTopBar}
      />
      {shouldShowStorefrontToggle && (
        <Box __css={styles.findLinkPlacement}>
          <FindLink />
        </Box>
      )}
    </StyledMainContainer>
  ) : null;
};

const ValuePropsTopBarItems = ({ cardsValueProps, toggleDrawer, activeValuePropVariant, fullGradientString, elementsAmount, closeValuePropsTopBar }: ValuePropsTopBarItemsArgs) => {
  const { config } = useShopStorefrontContext();
  return (
    <StyledValuePropBarVariantContainer __css={styles.valuePropBarVariantContainer} $fullGradientString={fullGradientString} $elementsAmount={elementsAmount}>
      {config.valuePropsTopBarOrder.map((item, idx) => {
        const valueProp: ValueProp = cardsValueProps[item];
        return (
          <StyledValuePropBarVariantWrapper key={item} __css={styles.valuePropBarVariantWrapper}>
            <ValuePropBarVariant onClick={toggleDrawer} title={valueProp.title} show={idx === activeValuePropVariant} />
          </StyledValuePropBarVariantWrapper>
        );
      })}
      <Box __css={styles.styledCloseButtonContainerStyles}>
        <IconButton variant="ghost" onClick={closeValuePropsTopBar} aria-label="close">
          <Close color="white" />
        </IconButton>
      </Box>
    </StyledValuePropBarVariantContainer>
  );
};

const ValuePropBarVariant = ({ onClick, title, show }: ValuePropBarVariantArgs) => {
  return (
    <StyledValuePropBarContainer as="button" __css={styles.valuePropBarContainer(show)} onClick={onClick}>
      <StyledTitle typographyVariant="body1" fontWeight={500}>
        {title}
      </StyledTitle>
      <ChevronDownSvg />
    </StyledValuePropBarContainer>
  );
};

const ValuePropCardsContainer = ({ cardsValueProps, onValuePropCardClick, valuePropDrawerPageTracker }: ValuePropCardsContainerArgs) => {
  const [isMobile] = useResponsive({ values: { mobile: true, tablet: true, desktop: false } });

  useTelemetryPageEvent(valuePropDrawerPageTracker);

  if (isMobile) {
    return (
      <StyledValuePropCardsContainer __css={styles.valuePropCardsContainerMobile}>
        <ValuePropCard cardData={cardsValueProps['shipping']} onClick={onValuePropCardClick} />
        <ValuePropCard cardData={cardsValueProps['completionDiscount']} onClick={onValuePropCardClick} />
        <ValuePropCard cardData={cardsValueProps['easyReturns']} onClick={onValuePropCardClick} />
        <ValuePropCard cardData={cardsValueProps['priceMatchPromise']} onClick={onValuePropCardClick} />
      </StyledValuePropCardsContainer>
    );
  }

  return (
    <StyledValuePropCardsContainer __css={styles.valuePropCardsContainerDesktop}>
      <Flex __css={styles.valuePropCardsColumnDesktop}>
        <ValuePropCard cardData={cardsValueProps['shipping']} objectPosition="right" onClick={onValuePropCardClick} />
        <ValuePropCard cardData={cardsValueProps['priceMatchPromise']} objectPosition="right" onClick={onValuePropCardClick} />
      </Flex>
      <Flex __css={styles.valuePropCardsColumnDesktop}>
        <ValuePropCard cardData={cardsValueProps['completionDiscount']} objectPosition="left" onClick={onValuePropCardClick} />
        <ValuePropCard cardData={cardsValueProps['easyReturns']} objectPosition="left" onClick={onValuePropCardClick} />
      </Flex>
    </StyledValuePropCardsContainer>
  );
};

const ValuePropCard = React.memo(({ cardData, objectPosition, onClick }: ValuePropCardArgs) => {
  const [isMobile] = useResponsive({ values: { mobile: true, tablet: true, desktop: false } });
  const imgSrc = isMobile ? cardData.cardImageUrlMobile : cardData.cardImageUrl;
  const linkProps = cardData.hasLink ? { href: cardData.targetUrl, target: '_blank' } : {};
  const as = cardData.hasLink ? 'a' : 'div';

  return (
    <StyledValuePropCard __css={styles.valuePropCard} as={as} onClick={() => onClick(cardData)} {...linkProps}>
      <StyledValuePropCardImg as="img" __css={styles.valuePropCardImg(objectPosition)} src={imgSrc} alt={cardData.title} />
    </StyledValuePropCard>
  );
});

export { ValuePropsTopBar };
